import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  useColorModeValue,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import { FiTrendingUp } from 'react-icons/fi';

const WeeklyAttendanceChart = ({ data, isLoading }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        p={6}
        borderRadius="lg"
        border="1px solid"
        borderColor={borderColor}
        shadow="sm"
      >
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" />
        </Flex>
      </Box>
    );
  }

  // Use processed weekly data directly
  const weeklyData = data?.weeklyData || [];
  const weeklyStats = data?.weeklyStats || {};
  const bestDay = data?.bestDay || null;
  const worstDay = data?.worstDay || null;

  return (
    <Box
      bg={cardBg}
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor={borderColor}
      shadow="sm"
    >
      <HStack mb={6}>
        <FiTrendingUp size={20} color="#4A5568" />
        <Heading size="sm" color="gray.900" fontWeight="600">
          Weekly Attendance Trends
        </Heading>
      </HStack>

      {/* Weekly Summary */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="md">
        <VStack spacing={3} align="start">
          <Text fontSize="lg" fontWeight="600" color="gray.900">
            Weekly Overview
          </Text>
          <HStack justify="space-between" w="full">
            <Text fontSize="sm" color="gray.600">Total Present:</Text>
            <Text fontSize="sm" fontWeight="600" color="green.600">{weeklyStats.totalPresent || 0}</Text>
          </HStack>
          <HStack justify="space-between" w="full">
            <Text fontSize="sm" color="gray.600">Total Absent:</Text>
            <Text fontSize="sm" fontWeight="600" color="red.600">{weeklyStats.totalAbsent || 0}</Text>
          </HStack>
          <HStack justify="space-between" w="full">
            <Text fontSize="sm" color="gray.600">Total Leave:</Text>
            <Text fontSize="sm" fontWeight="600" color="orange.600">{weeklyStats.totalLeave || 0}</Text>
          </HStack>
          <HStack justify="space-between" w="full">
            <Text fontSize="sm" color="gray.600">Total Late:</Text>
            <Text fontSize="sm" fontWeight="600" color="yellow.600">{weeklyStats.totalLate || 0}</Text>
          </HStack>
          <HStack justify="space-between" w="full" pt={2} borderTop="1px solid" borderColor="gray.200">
            <Text fontSize="md" fontWeight="600" color="gray.700">Weekly Attendance Rate:</Text>
            <Text fontSize="md" fontWeight="700" color={(weeklyStats.weeklyAttendanceRate || 0) >= 90 ? 'green.600' : (weeklyStats.weeklyAttendanceRate || 0) >= 75 ? 'yellow.600' : 'red.600'}>
              {weeklyStats.weeklyAttendanceRate || 0}%
            </Text>
          </HStack>
        </VStack>
      </Box>

      {/* Daily Breakdown */}
      <VStack spacing={4} align="stretch">
        <Text fontSize="md" fontWeight="600" color="gray.900" mb={2}>
          Daily Breakdown
        </Text>
        {weeklyData.map((day, index) => (
          <Box key={index} p={3} border="1px solid" borderColor="gray.200" borderRadius="md">
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="gray.700">
                {day.day}
              </Text>
              <HStack spacing={2}>
                <Text fontSize="xs" color="green.600">P: {day.present}</Text>
                <Text fontSize="xs" color="red.600">A: {day.absent}</Text>
                <Text fontSize="xs" color="orange.600">L: {day.leave}</Text>
                <Text fontSize="xs" color="yellow.600">Late: {day.late}</Text>
              </HStack>
            </HStack>
            <Box
              width="100%"
              height="6px"
              bg="gray.100"
              borderRadius="full"
              overflow="hidden"
            >
              <Box
                height="100%"
                width={`${day.attendanceRate}%`}
                bg={day.attendanceRate >= 90 ? '#48BB78' : day.attendanceRate >= 75 ? '#ECC94B' : '#F56565'}
                borderRadius="full"
                transition="width 0.3s ease"
              />
            </Box>
            <HStack justify="space-between" mt={1}>
              <Text fontSize="xs" color="gray.500">
                {day.attendanceRate}% attendance
              </Text>
              <Text fontSize="xs" color="gray.500">
                {day.total} employees
              </Text>
            </HStack>
          </Box>
        ))}
      </VStack>

      {/* Insights */}
      <Box mt={6} p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
        <VStack spacing={2} align="start">
          <Text fontSize="sm" fontWeight="600" color="blue.900">
            Weekly Insights
          </Text>
          <Text fontSize="xs" color="blue.700">
            📈 Best Day: <strong>{bestDay?.day || 'N/A'}</strong> ({bestDay?.attendanceRate || 0}% attendance)
          </Text>
          <Text fontSize="xs" color="blue.700">
            📉 Needs Attention: <strong>{worstDay?.day || 'N/A'}</strong> ({worstDay?.attendanceRate || 0}% attendance)
          </Text>
          <Text fontSize="xs" color="blue.700">
            🎯 Overall: {(weeklyStats.weeklyAttendanceRate || 0) >= 90 ? 'Excellent' : (weeklyStats.weeklyAttendanceRate || 0) >= 75 ? 'Good' : 'Needs Improvement'} weekly performance
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default WeeklyAttendanceChart; 